// ─────────────────────────────────────────────────────────────────────────────
//  ServiceStockSettings — lab and scan stock enforcement
//
//  The pharmacy equivalent needed no setup: dispensary_bal already held a
//  balance per drug. The laboratory had nothing of the kind — no column in this
//  schema joined a test to the reagent, tube or film it uses — so this panel
//  carries BOTH halves:
//
//    1. the two switches (lab and scan, independent)
//    2. the mapping of test -> consumable + quantity, without which a switch
//       has nothing to check
//
//  Both switches default OFF, unlike the pharmacy one. The pharmacy default
//  preserved behaviour that already existed; lab enforcement is new, and
//  switching it on by default would start refusing orders against an empty
//  consumables table the moment the server was upgraded.
//
//  The rule that makes it safe: A TEST WITH NO MAPPING IS NEVER REFUSED. So the
//  panel leads with how many are mapped — a switch turned on with nothing
//  mapped does nothing at all, and the screen has to say so rather than looking
//  broken.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Alert, Divider, Chip, Button, Stack,
  RadioGroup, Radio, FormControl, FormLabel, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, IconButton, Tooltip, TextField,
  Autocomplete, MenuItem, Select, InputLabel, Snackbar, CircularProgress, alpha,
} from '@mui/material';
import {
  DeleteOutline, AddCircleOutline, ScienceOutlined, CameraAltOutlined,
} from '@mui/icons-material';
import axios from 'axios';

import {
  serviceStockSetting, serviceConsumables as serviceConsumablesUrl,
  deleteServiceConsumable,
} from '../BaseURL';

const OPTIONS = (kind) => ([
  {
    value: 'off',
    label: 'Do not enforce — order anything, any time',
    desc: `Stock is still recorded and still deducted when a result is entered; it simply never refuses an order. This is how the system has always behaved.`,
    color: 'success.main',
  },
  {
    value: 'enforce',
    label: `Enforce stock`,
    desc: `A ${kind === 'scan' ? 'scan' : 'test'} whose consumables are recorded and short cannot be ordered — the request is refused. ${kind === 'scan' ? 'Scans' : 'Tests'} with nothing recorded are never refused.`,
    color: 'warning.main',
  },
]);

export default function ServiceStockSettings() {
  const [lab, setLab]   = useState(false);
  const [scan, setScan] = useState(false);
  const [mapped, setMapped] = useState({ lab: 0, scan: 0 });
  const [migrationApplied, setMigrationApplied] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Mapping editor
  const [mappings, setMappings] = useState([]);
  const [services, setServices] = useState({ lab: [], scan: [] });
  const [consumables, setConsumables] = useState([]);
  const [draft, setDraft] = useState({ serviceType: 'lab', serviceName: '', consumableId: '', qtyPerTest: '1' });

  const say = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const loadSetting = useCallback(async () => {
    try {
      const r = await axios.get(serviceStockSetting);
      if (r.data?.success) {
        setLab(!!r.data.lab);
        setScan(!!r.data.scan);
        setMapped(r.data.mapped || { lab: 0, scan: 0 });
        setMigrationApplied(r.data.migrationApplied !== false);
      }
    } catch (_) { /* leave the safe default: both off */ }
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const r = await axios.get(serviceConsumablesUrl);
      setMigrationApplied(r.data?.migrationApplied !== false);
      setMappings(r.data?.mappings || []);
      setServices(r.data?.services || { lab: [], scan: [] });
      setConsumables(r.data?.consumables || []);
    } catch (_) { /* the panel still shows the switches */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([loadSetting(), loadMappings()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loadSetting, loadMappings]);

  const setKind = async (kind, value) => {
    const enabled = value === 'enforce';
    const prev = kind === 'lab' ? lab : scan;
    if (enabled === prev) return;
    (kind === 'lab' ? setLab : setScan)(enabled);   // optimistic
    setSaving(true);
    try {
      const r = await axios.put(serviceStockSetting, { kind, enabled });
      if (!r.data?.success) throw new Error(r.data?.message || 'Save failed');
      const n = mapped[kind] || 0;
      say(
        enabled
          ? (n === 0
              ? `${kind === 'lab' ? 'Lab' : 'Scan'} enforcement is on, but nothing is mapped yet — so nothing will be refused until you add requirements below.`
              : `${kind === 'lab' ? 'Lab' : 'Scan'} enforcement is on for the ${n} mapped ${kind === 'lab' ? 'test' : 'scan'}${n === 1 ? '' : 's'}.`)
          : `${kind === 'lab' ? 'Lab' : 'Scan'} orders will no longer be refused for stock.`,
        enabled && n === 0 ? 'warning' : 'success'
      );
    } catch (err) {
      (kind === 'lab' ? setLab : setScan)(prev);    // roll back so it never lies
      say(err.message || 'Could not save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addMapping = async () => {
    if (!draft.serviceName || !draft.consumableId) {
      say('Pick a test and a consumable first', 'error');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(serviceConsumablesUrl, {
        serviceType: draft.serviceType,
        serviceName: draft.serviceName,
        consumableId: draft.consumableId,
        qtyPerTest: Number(draft.qtyPerTest),
      });
      if (!r.data?.success) throw new Error(r.data?.message || 'Save failed');
      setDraft(d => ({ ...d, serviceName: '', consumableId: '', qtyPerTest: '1' }));
      await Promise.all([loadMappings(), loadSetting()]);
      say('Requirement saved');
    } catch (err) {
      say(err.response?.data?.message || err.message || 'Could not save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const removeMapping = async (id) => {
    setSaving(true);
    try {
      await axios.delete(deleteServiceConsumable(id));
      await Promise.all([loadMappings(), loadSetting()]);
      say('Requirement removed');
    } catch (err) {
      say(err.response?.data?.message || 'Could not remove', 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderSwitch = (kind) => {
    const value = (kind === 'lab' ? lab : scan) ? 'enforce' : 'off';
    const n = mapped[kind] || 0;
    return (
      <FormControl component="fieldset" fullWidth disabled={loading || saving || !migrationApplied}>
        <FormLabel
          component="legend"
          sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.primary', mb: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {kind === 'lab' ? <ScienceOutlined fontSize="small" /> : <CameraAltOutlined fontSize="small" />}
            <span>{kind === 'lab' ? 'LABORATORY — REAGENTS & CONSUMABLES' : 'SCAN — FILM & CONSUMABLES'}</span>
            <Chip
              size="small"
              label={n === 0 ? 'nothing mapped' : `${n} mapped`}
              color={n === 0 ? 'default' : 'primary'}
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>
        </FormLabel>
        <RadioGroup value={value} onChange={(e) => setKind(kind, e.target.value)}>
          {OPTIONS(kind).map((opt) => (
            <Box
              key={opt.value}
              onClick={() => { if (!loading && !saving && migrationApplied) setKind(kind, opt.value); }}
              sx={{
                display: 'flex', alignItems: 'flex-start', p: 1.1, mb: 0.8, borderRadius: 2,
                border: '1.5px solid', cursor: 'pointer', transition: 'all 0.18s',
                borderColor: value === opt.value ? opt.color : 'divider',
                bgcolor: value === opt.value ? alpha('#1a6b8a', 0.04) : 'transparent',
              }}
            >
              <Radio value={opt.value} size="small" sx={{ mt: -0.3, color: opt.color }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.83rem' }}>
                  {opt.label}
                  {opt.value === 'off' && (
                    <Chip size="small" label="Default" sx={{ ml: 1, height: 17, fontSize: '0.62rem' }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const nameOptions = services[draft.serviceType] || [];

  return (
    <Card>
      <CardContent>
        {!migrationApplied && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <b>Migration 034 has not been applied to this database.</b> The switches
            below cannot take effect until it is — run
            <code> backend/migrations/034_lab_scan_consumable_requirements.sql</code>,
            then restart the API server.
          </Alert>
        )}

        {/* This card and "Lab & Scan — PAYMENT gate" had near-identical names and
            sat in different sections of the settings page, so this one kept
            getting changed when payment was what was actually meant. The line
            below used to be a passing mention inside the grey paragraph; it is
            now an alert, because that is the mistake people actually make. */}
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <b>Looking for “order labs without payment”? This is not that card.</b>
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            Setting this to <i>Do not enforce</i> does <b>not</b> make unpaid orders
            visible to the laboratory. For that, go to{' '}
            <b>System Settings → Billing &amp; Authorization → “Lab &amp; Scan —
            PAYMENT gate (unpaid orders)”</b> and choose{' '}
            <i>Show unpaid orders and allow results</i>. Unpaid orders are hidden
            by default.
          </Box>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Whether a test or scan can be ordered when the things it consumes have run
          out. This is about <b>availability</b> — reagents and film — and nothing
          to do with payment. Enforcement is checked on the <b>server</b>, so a
          stale page cannot slip an order past it.
        </Typography>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} /><Typography variant="body2">Loading…</Typography>
          </Stack>
        ) : (
          <>
            {renderSwitch('lab')}
            <Divider sx={{ my: 2 }} />
            {renderSwitch('scan')}

            <Divider sx={{ my: 2 }} />

            {/* ── the mapping, without which the switches do nothing ────────── */}
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.5 }}>
              WHAT EACH TEST CONSUMES
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              A test is only ever refused for stock if it appears here. Add the few that
              matter — on a live database the 20 most-ordered items covered 95% of every
              order ever placed, so a short list goes a long way. Consumables and their
              balances are maintained in <b>Procurement → Consumables</b>.
            </Typography>

            {consumables.length === 0 && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                No consumables have been created yet, so there is nothing to map a test
                to. Add them in <b>Procurement → Consumables</b> first — reagents, tubes,
                films, gel — then come back here and say which test uses what.
              </Alert>
            )}

            {/* Add row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }} alignItems="stretch">
              <FormControl size="small" sx={{ minWidth: 92 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={draft.serviceType}
                  onChange={(e) => setDraft(d => ({ ...d, serviceType: e.target.value, serviceName: '' }))}
                >
                  <MenuItem value="lab">Lab</MenuItem>
                  <MenuItem value="scan">Scan</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                freeSolo
                forcePopupIcon
                openOnFocus
                size="small"
                sx={{ flex: 1.4, minWidth: 180 }}
                options={nameOptions}
                value={draft.serviceName}
                onChange={(_e, v) => setDraft(d => ({ ...d, serviceName: typeof v === 'string' ? v : (v ?? '') }))}
                onInputChange={(_e, v, reason) => { if (reason === 'input') setDraft(d => ({ ...d, serviceName: v })); }}
                renderInput={(p) => <TextField {...p} label={draft.serviceType === 'lab' ? 'Test' : 'Scan'} />}
              />

              <FormControl size="small" sx={{ flex: 1.2, minWidth: 170 }}>
                <InputLabel>Consumable</InputLabel>
                <Select
                  label="Consumable"
                  value={draft.consumableId}
                  onChange={(e) => setDraft(d => ({ ...d, consumableId: e.target.value }))}
                >
                  {consumables.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} — {c.stock_qty} {c.base_unit || ''} in stock
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small" label="Qty / test" type="number"
                sx={{ width: 108 }}
                inputProps={{ min: 0, step: '0.01' }}
                value={draft.qtyPerTest}
                onChange={(e) => setDraft(d => ({ ...d, qtyPerTest: e.target.value }))}
              />

              <Button
                variant="contained" size="small" disableElevation
                startIcon={<AddCircleOutline />}
                onClick={addMapping}
                disabled={saving || !migrationApplied || consumables.length === 0}
                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Stack>

            {mappings.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Nothing mapped yet — so no order can be refused for stock, whatever the
                switches above say.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7fafc' }}>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700 }}>TYPE</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700 }}>TEST / SCAN</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700 }}>CONSUMES</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700 }} align="right">QTY</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700 }} align="right">IN STOCK</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((m) => {
                      const short = Number(m.stock_qty) < Number(m.qty_per_test);
                      return (
                        <TableRow key={m.id} hover>
                          <TableCell>
                            <Chip size="small" variant="outlined"
                              label={m.service_type === 'scan' ? 'Scan' : 'Lab'}
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{m.service_name}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {m.consumable_name || <em style={{ color: '#c62828' }}>deleted consumable</em>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                            {Number(m.qty_per_test)} {m.base_unit || ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                            <Typography component="span"
                              sx={{ fontSize: '0.8rem', fontWeight: short ? 700 : 400,
                                    color: short ? 'error.main' : 'text.primary' }}>
                              {Number(m.stock_qty)}
                            </Typography>
                            {short && (
                              <Chip size="small" color="error" label="short"
                                sx={{ ml: 0.75, height: 17, fontSize: '0.62rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove this requirement">
                              <IconButton size="small" color="error" onClick={() => removeMapping(m.id)} disabled={saving}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              Stock is deducted when a <b>result is entered</b>, not when the order is
              placed — that is the point at which the reagent or film is actually gone.
              Recording a result is never refused: once a test has been run, the finding
              has to be saved whatever the balance says.
            </Alert>
          </>
        )}
      </CardContent>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Card>
  );
}
